import React, { useEffect, useState } from 'react';
import ProjectCard from '../components/ProjectCard';
import StripesBackground from '../components/StripesBackground';
import { projectsAPI } from '../utils/api';

const Projects = () => {
  const [allProjects, setAllProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [categoryFilter, roleFilter, allProjects]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await projectsAPI.getAll();
      setAllProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...allProjects];

    // Apply category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(p => p.category === categoryFilter);
    }

    // Apply role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter(p => {
        const role = p.role || '';
        if (roleFilter === 'VFX Supervisor') {
          return role === 'VFX Supervisor' || role === 'Preprod/On-Set Supervisor';
        } else if (roleFilter === '2D Supervisor') {
          return role === '2D Supervisor';
        } else if (roleFilter === 'Compositor') {
          // Everything else goes to Compositor
          return !['VFX Supervisor', '2D Supervisor', 'Preprod/On-Set Supervisor'].includes(role);
        }
        return true;
      });
    }

    setFilteredProjects(filtered);
  };

  const categories = [
    { value: 'all', label: 'ALL PROJECTS' },
    { value: 'Feature Films', label: 'FEATURE FILMS' },
    { value: 'TV', label: 'TV' },
    { value: 'Others', label: 'OTHERS' },
  ];

  const roles = [
    { value: 'all', label: 'ALL ROLES' },
    { value: 'VFX Supervisor', label: 'VFX SUPERVISOR' },
    { value: '2D Supervisor', label: '2D SUPERVISOR' },
    { value: 'Compositor', label: 'COMPOSITOR' },
  ];

  return (
    <div className="min-h-screen bg-black pt-20 pb-24" data-testid="projects-page">
      <div className="relative py-16">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(255,255,255,0.1) 2px, rgba(255,255,255,0.1) 4px)',
          }}
        />
        
        <div className="container mx-auto px-6 relative z-10">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-white mb-4 font-oswald tracking-wide" data-testid="projects-title">Some of my work</h1>
            <div className="w-32 h-0.5 bg-white mx-auto mb-8"></div>
          </div>

          {/* Category Filter Buttons */}
          <div className="flex justify-center gap-4 mb-6 flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat.value}
                onClick={() => setCategoryFilter(cat.value)}
                className={`px-6 py-2 text-sm tracking-widest transition-all ${
                  categoryFilter === cat.value
                    ? 'bg-white text-black'
                    : 'bg-transparent text-gray-300 hover:bg-white hover:bg-opacity-10 border border-gray-600'
                }`}
                data-testid={`filter-${cat.value}`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Role Filter Buttons */}
          <div className="flex justify-center gap-4 mb-12 flex-wrap">
            {roles.map((role) => (
              <button
                key={role.value}
                onClick={() => setRoleFilter(role.value)}
                className={`px-6 py-2 text-sm tracking-widest transition-all ${
                  roleFilter === role.value
                    ? 'bg-white text-black'
                    : 'bg-transparent text-gray-300 hover:bg-white hover:bg-opacity-10 border border-gray-600'
                }`}
                data-testid={`role-filter-${role.value}`}
              >
                {role.label}
              </button>
            ))}
          </div>

          {/* Projects Grid */}
          {loading ? (
            <div className="text-center py-20">
              <p className="text-gray-400">Loading projects...</p>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-400">No projects found.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {filteredProjects.map((project) => (
                <ProjectCard key={project.id} project={project} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Projects;
